/**
 * getComparableJobOffers — read-only helper that surfaces past jobs whose
 * installer was actually paid, so dispatchers can reality-check Offer
 * Compensation against REAL accepted economics.
 *
 * Integrity contract:
 *   • Comparable economics come ONLY from immutable accepted
 *     `MarketplaceOffer.payoutAmountSnapshot` values. `Job.payoutAmount`
 *     (an editable baseline) is never used as the comparable amount.
 *   • Cancelled, rescission, and finance-declined jobs are excluded via
 *     canonical fields:
 *        - `Job.rescissionStatus !== "In Rescission"`
 *        - linked `Opportunity.wonOutcome` not in {canceled, finance_declined}
 *   • Zero / missing snapshots are excluded (no placeholder economics).
 *   • The target job itself is excluded.
 *
 * Display gating (caller responsibility, but reflected in the returned
 * `hasDisplayableSample` flag):
 *   • Suggested band, median, min, max, and recommendation copy
 *     render ONLY when `count >= MIN_SAMPLE_FOR_DISPLAY` (=3).
 *   • Below that, the caller should show "Not enough accepted comparable
 *     jobs yet." and skip the band entirely.
 *
 * Pure derivation. No mutations.
 */
import type { Job } from "@/data/types";
import type { Opportunity } from "@/data/opportunityTypes";
import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";
import {
  MIN_SAMPLE_FOR_DISPLAY,
  hasDisplayableSample,
} from "@/lib/analyticsThresholds";

export interface ComparableJob {
  job: Job;
  /** Accepted MarketplaceOffer.payoutAmountSnapshot — canonical comparable. */
  acceptedPayoutSnapshot: number;
  /** ISO timestamp of the accepted offer used. */
  acceptedAt: string;
  city?: string;
}

export interface ComparableJobOffersSummary {
  matches: ComparableJob[];
  /** Total number of accepted comparable jobs found (not just `matches` slice). */
  count: number;
  /** All numeric stats are NULL when count < MIN_SAMPLE_FOR_DISPLAY. */
  median: number | null;
  min: number | null;
  max: number | null;
  /** True iff caller may render the suggested band and stats. */
  hasDisplayableSample: boolean;
  /** Echoed for caller copy ("Need N more"). */
  minSample: number;
}

interface Inputs {
  job: Job;
  jobs: Job[];
  offers: MarketplaceOffer[];
  opportunities: Opportunity[];
  limit?: number;
}

/** Latest accepted, snapshot>0 offer for a job, or null. */
function latestAcceptedOffer(
  jobId: string,
  offers: MarketplaceOffer[],
): MarketplaceOffer | null {
  const accepted = offers.filter(
    (o) =>
      o.jobId === jobId &&
      o.status === "accepted" &&
      typeof o.payoutAmountSnapshot === "number" &&
      o.payoutAmountSnapshot > 0,
  );
  if (accepted.length === 0) return null;
  accepted.sort(
    (a, b) =>
      new Date(b.respondedAt ?? b.sentAt).getTime() -
      new Date(a.respondedAt ?? a.sentAt).getTime(),
  );
  return accepted[0];
}

/** Returns true when the linked opportunity disqualifies the job. */
function isJobOutcomeExcluded(
  jobId: string,
  opportunities: Opportunity[],
): boolean {
  const linked = opportunities.find((o) => o.linkedJobId === jobId);
  if (!linked) return false; // no fragile heuristic — accepted-offer gate carries the integrity
  if (linked.wonOutcome === "canceled") return true;
  if (linked.wonOutcome === "finance_declined") return true;
  return false;
}

export function getComparableJobOffers({
  job,
  jobs,
  offers,
  opportunities,
  limit = 5,
}: Inputs): ComparableJobOffersSummary {
  const city = job.address?.city?.trim().toLowerCase() ?? null;

  type Match = ComparableJob & { _sortTs: number };
  const enriched: Match[] = [];

  for (const j of jobs) {
    if (j.id === job.id) continue;
    if (j.projectType !== job.projectType) continue;
    if (j.rescissionStatus === "In Rescission") continue;
    if (isJobOutcomeExcluded(j.id, opportunities)) continue;

    // Geo: same city when present, else same region.
    if (city) {
      const jc = j.address?.city?.trim().toLowerCase();
      if (jc !== city && j.region !== job.region) continue;
    } else if (j.region !== job.region) {
      continue;
    }

    const accepted = latestAcceptedOffer(j.id, offers);
    if (!accepted) continue; // accepted-offer gate (no baseline fallback)

    enriched.push({
      job: j,
      acceptedPayoutSnapshot: accepted.payoutAmountSnapshot as number,
      acceptedAt: accepted.respondedAt ?? accepted.sentAt,
      city: j.address?.city,
      _sortTs: new Date(accepted.respondedAt ?? accepted.sentAt).getTime(),
    });
  }

  enriched.sort((a, b) => b._sortTs - a._sortTs);

  const count = enriched.length;
  const displayable = hasDisplayableSample(count);

  let median: number | null = null;
  let min: number | null = null;
  let max: number | null = null;
  if (displayable) {
    const sorted = enriched
      .map((e) => e.acceptedPayoutSnapshot)
      .sort((a, b) => a - b);
    min = sorted[0];
    max = sorted[sorted.length - 1];
    median =
      sorted.length % 2
        ? sorted[(sorted.length - 1) / 2]
        : Math.round((sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2);
  }

  const matches: ComparableJob[] = enriched.slice(0, limit).map((m) => ({
    job: m.job,
    acceptedPayoutSnapshot: m.acceptedPayoutSnapshot,
    acceptedAt: m.acceptedAt,
    city: m.city,
  }));

  return {
    matches,
    count,
    median,
    min,
    max,
    hasDisplayableSample: displayable,
    minSample: MIN_SAMPLE_FOR_DISPLAY,
  };
}
